const { isArray } = Array;

function baseIsPrimitive(kindOrPredicate) {
    const isKind = typeof kindOrPredicate === 'string';
    return function baseIsPrimitiveOfKind(tag) {
        return function baseIsPrimitiveOfKindTag(value) {
            const t = typeof value;
            return (isKind && t === kindOrPredicate)
                || (!isKind && kindOrPredicate && kindOrPredicate(value))
                || (t === 'object'
                    && ((isArray(tag) && tag.includes(Object.prototype.toString.call(value)))
                        || Object.prototype.toString.call(value) === tag));
        };
    };
}

const isDate = /* #__PURE__ */ baseIsPrimitive()([
    '[object Date]',
]);

const isNumber = /* #__PURE__ */ baseIsPrimitive('number')('[object Number]');

/**
 * ECMA standard "date time string format" is: `YYYY-MM-DDTHH:mm:ss.sssZ`
 *
 * Various components can be omitted, so the following are all valid:
 *  - Date-only form: YYYY, YYYY-MM, YYYY-MM-DD
 *  - Date-time form: one of the above date-only forms, followed by T, followed by HH:mm, HH:mm:ss, or HH:mm:ss.sss. Each combination can be followed by a time zone offset.
 * For example, "2011-10-10" (date-only form), "2011-10-10T14:48:00" (date-time form), or "2011-10-10T14:48:00.000+09:00" (date-time form with milliseconds and time zone) are all valid date time strings.
 *
 * When the time zone offset is absent, date-only forms are interpreted as a UTC time and date-time forms are interpreted as a local time. The interpretation as a UTC time is due to a historical spec error that was not consistent with ISO 8601 but could not be changed due to web compatibility.
 *
 * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#date_time_string_format
 */
const interpretedAsUtcRegExp = /(Z|\+00:?00)$|^\d{13}$|^\d{10}$|^\d{4}(-\d{2}(-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?(Z|\+00:?00))?)?)?$/;
const offsetRegExp = /Z$|([+-])(\d{2}):?(\d{2})/;
const detectDateStringOffset = (input) => {
    const inputString = String(input);
    if (interpretedAsUtcRegExp.test(inputString)) {
        return 0;
    }
    const result = inputString.match(offsetRegExp);
    if (!result) {
        return null;
    }
    if (result[0] === 'Z') {
        return 0;
    }
    const sign = result[1];
    const hours = Number(result[2]);
    const minutes = Number(result[3]);
    const total = (hours * 60) + minutes;
    return total && sign === '+' ? (total * -1) : total;
};

/**
 * Converts raw number, string, or Date to a Date object.
 */
const toDate = (input) => {
    const inputAsString = String(input);
    // Check if input is a raw UNIX timestamp (must convert to Date)
    if (inputAsString.match(/^\d{5,}$$/)) {
        if (inputAsString.length === 10) {
            return new Date(Number(inputAsString) * 1000);
        }
        if (inputAsString.length === 13) {
            return new Date(Number(inputAsString));
        }
        return null;
    }
    try {
        return new Date(input);
    }
    catch {
        return null;
    }
};
const dateToString = (input, asUtcOrTimezoneOffset, glue = '/') => {
    let timezoneOffset = 0;
    const date = toDate(input);
    const localOffset = date?.getTimezoneOffset() ?? 0;
    if (isDate(input)) {
        if (isNumber(asUtcOrTimezoneOffset)) {
            timezoneOffset = (asUtcOrTimezoneOffset * -1) + localOffset;
        }
    }
    else {
        const inputOffset = detectDateStringOffset(input);
        if (isNumber(inputOffset)) {
            // if there is an offset is in the input, we need to invert it
            const effectiveInputOffset = inputOffset ? (inputOffset * -1) : 0;
            timezoneOffset = effectiveInputOffset + localOffset;
        }
    }
    // Invalid date
    if (!date || Number.isNaN(date.getTime())) {
        return null;
    }
    // apply offset
    date.setMinutes(date.getMinutes() + timezoneOffset);
    return `${date.getFullYear()}${glue}${(`0${date.getMonth() + 1}`).slice(-2)}${glue}${(`0${date.getDate()}`).slice(-2)}`;
};

/**
 * Calc the difference between dates, in days.
 *
 * @param {number|string} rawArrival - a date
 * @param {number|string} rawDeparture - a date
 * @return {number} - Number of days between both dates.
 */
function daysBetween(rawArrival, rawDeparture) {
    let parts;
    let parsedArrival;
    let parsedDeparture;
    let rawArrivalParam = rawArrival;
    let rawDepartureParam = rawDeparture;
    if (typeof rawArrival === 'number') {
        rawArrivalParam = String(rawArrivalParam);
    }
    if (typeof rawDeparture === 'number') {
        rawDepartureParam = String(rawDepartureParam);
    }
    if (typeof rawArrivalParam === 'string') {
        parts = rawArrivalParam.substring(0, 10).split(/[/-]/).map(el => Number(el));
        // it is a THN date string yyyy/mm/dd
        if (parts.length > 2) {
            parsedArrival = new Date(parts[0], parts[1] - 1, parts[2]);
        }
        else { // unixtimestamp
            parts = rawArrivalParam.match(/^\d{10}/)?.map(el => parseInt(el, 10)) || [];
            if (parts?.length) {
                parsedArrival = new Date(parts[0] * 1000);
            }
        }
    }
    else if (rawArrivalParam instanceof Date) {
        // Copy date parts of the timestamps, discarding the time parts.
        parsedArrival = new Date(rawArrivalParam.getFullYear(), rawArrivalParam.getMonth(), rawArrivalParam.getDate());
    }
    if (typeof rawDepartureParam === 'string') {
        parts = rawDepartureParam.substring(0, 10).split(/[/-]/).map(el => Number(el));
        // it is a THN date string yyyy/mm/dd
        if (parts.length > 2) {
            parsedDeparture = new Date(parts[0], parts[1] - 1, parts[2]);
        }
        else { // unixtimestamp
            parts = rawDepartureParam.match(/^\d{10}/)?.map(el => parseInt(el, 10)) || [];
            if (parts?.length) {
                parsedDeparture = new Date(parts[0] * 1000);
            }
        }
    }
    else if (rawDepartureParam instanceof Date) {
        // Copy date parts of the timestamps, discarding the time parts.
        parsedDeparture = new Date(rawDepartureParam.getFullYear(), rawDepartureParam.getMonth(), rawDepartureParam.getDate());
    }
    // Do the math.
    const millirawDeparturesPerDay = 1000 * 60 * 60 * 24;
    if (!parsedArrival || !parsedDeparture) {
        throw new Error('Unable to parse dates.');
    }
    // correct offsets: in case they are in a diferent DST
    parsedArrival.setMinutes(parsedArrival.getMinutes() - parsedArrival.getTimezoneOffset());
    parsedDeparture.setMinutes(parsedDeparture.getMinutes() - parsedDeparture.getTimezoneOffset());
    // diff
    const millisBetween = parsedDeparture.getTime() - parsedArrival.getTime();
    const days = millisBetween / millirawDeparturesPerDay;
    // Use round to avoid some timezone errors in the JS engine
    // (for instance Brazil/East on DST)
    return Math.round(days);
}

export { daysBetween as a, dateToString as d };
